import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCompetences, createCompetence } from '../api/api';

// Create Context
const CompetenceContext = createContext(null);

// Provider Component
export const CompetenceProvider = ({ children }) => {
    const [globalCompetence, setGlobalCompetenceState] = useState(() => {
        // Initialize from localStorage
        return localStorage.getItem('globalCompetence') || '';
    });
    const [availableMonths, setAvailableMonths] = useState([]);
    const [competenceData, setCompetenceData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch available months on mount
    useEffect(() => {
        const fetchMonths = async () => {
            console.log("CompetenceContext: fetching months...");
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.log("CompetenceContext: No token found.");
                    setLoading(false);
                    return;
                }

                const res = await getCompetences();
                console.log("CompetenceContext: API Response:", res);
                if (res.data && Array.isArray(res.data)) {
                    setCompetenceData(res.data);
                    const months = res.data.map(c => c.mes);
                    console.log("CompetenceContext: Available months:", months);
                    setAvailableMonths(months);

                    // If no global competence set, use the most recent
                    if (!globalCompetence && months.length > 0) {
                        setGlobalCompetence(months[0]);
                    }
                } else {
                    console.warn("CompetenceContext: Invalid data format:", res.data);
                }
            } catch (error) {
                console.error("Erro ao carregar competências:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMonths();
    }, []);

    // Wrapper to persist to localStorage
    const setGlobalCompetence = useCallback((value) => {
        setGlobalCompetenceState(value);
        if (value) {
            localStorage.setItem('globalCompetence', value);
        }
    }, []);

    // Refresh months (can be called after new data import)
    const refreshMonths = useCallback(async () => {
        try {
            const res = await getCompetences();
            if (res.data && Array.isArray(res.data)) {
                setCompetenceData(res.data);
                const months = res.data.map(c => c.mes);
                setAvailableMonths(months);
            }
        } catch (error) {
            console.error("Erro ao atualizar competências:", error);
        }
    }, []);

    // Add a new competence locally (to allow selecting it before data exists)
    const addCompetence = useCallback(async (newMonth) => {
        if (newMonth) {
            try {
                // Try to persist to backend
                await createCompetence(newMonth);
                await refreshMonths();
            } catch (e) {
                console.error("Erro ao salvar competência no backend:", e);
                // Fallback local update if backend fails (though ideally we should show error)
                if (!availableMonths.includes(newMonth)) {
                    setAvailableMonths(prev => [...prev, newMonth].sort().reverse());
                }
            }
            setGlobalCompetence(newMonth);
        }
    }, [availableMonths, setGlobalCompetence, refreshMonths]);

    const isCompetenceClosed = useCallback((mes) => {
        const comp = competenceData.find(c => c.mes === mes);
        return comp ? comp.status === 'closed' : false;
    }, [competenceData]);

    const value = {
        globalCompetence,
        setGlobalCompetence,
        availableMonths,
        competenceData,
        refreshMonths,
        addCompetence,
        isCompetenceClosed,
        loading
    };

    return (
        <CompetenceContext.Provider value={value}>
            {children}
        </CompetenceContext.Provider>
    );
};

// Custom Hook
export const useCompetence = () => {
    const context = useContext(CompetenceContext);
    if (!context) {
        // Return a fallback for components outside the provider
        return {
            globalCompetence: localStorage.getItem('globalCompetence') || '',
            setGlobalCompetence: () => { },
            availableMonths: [],
            refreshMonths: () => { },
            loading: false
        };
    }
    return context;
};

export default CompetenceContext;
